/**
 * M6 Explorer (v0.2) — workpackage × branch × laag-tree.
 *
 * Hangt boven de huidige `ConceptExplorer` als nieuwe paneel-component.
 * Per spec [`architectuur-editor-explorer-dom.md`](docs/architecture/
 * architectuur-editor-explorer-dom.md) §5: hoofdmappen Archimate /
 * C4 / Sharp / Drawing per workpackage × branch met sub-groepen per
 * type + diagram-type, counts en signal-kleuren.
 *
 * v0.2 levert (ten opzichte van v0.1):
 * - Detail-paneel onder geselecteerd item: voor objecten Eigenschappen
 *   + Relaties + Diagram-voorkomens; voor diagrammen alleen basisinfo
 * - Multi-select: Cmd/Ctrl+klik toggelt losse items, Shift+klik
 *   selecteert bereik tussen vorige en huidige klik (binnen één
 *   sub-groep)
 * - "Open in diagram"-popup: klik op een diagram-naam in de lijst
 *   springt naar dat diagram (selecteert het in workspace-state)
 *
 * Niet in v0.2 (volgende fase):
 * - Inline bewerken van eigenschappen (read-only nu)
 * - Drag-naar-canvas (valt nog op de bestaande ConceptExplorer)
 * - Context-menu acties (Create / Rename / Delete)
 */

import type { AttributeSchema, BilingualText, Concept, Relation } from "@ae/domain";
import {
  Fragment,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  type BranchSummary,
  type DiagramDetailDTO,
  type DiagramOccurrenceDTO,
  type ExplorerConceptItem,
  type ExplorerDiagramItem,
  type ExplorerTreeDTO,
  type ObjectDetailDTO,
  type WorkPackageDTO,
} from "../../api/client";
import { GlossaryInfoButton } from "../../help/uiGlossary";
import { getRegistry, resolveBilingual, resolveConceptName } from "../../registry";
import { useKeymap } from "../../state/keymap";
import { isAdminOfWorkspace, useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { confirmHardDelete } from "../cascadeConfirm";
import { confirmDialog } from "../confirmDialog";
import { RoadmapMatrixConfigBlock } from "../diagrams/RoadmapMatrixConfigBlock";
import { explorerOwnsDeleteKey, isEditableTarget } from "../keyboardGuards";
import { NewBranchModal } from "../NewBranchModal";
import { PanelCollapseIcon } from "../PanelCollapseIcon";
import { usePanelWidth } from "../usePanelWidth";
import {
  EXISTING_CONCEPT_BULK_MIME,
  EXISTING_CONCEPT_MIME,
  EXISTING_DIAGRAM_MIME,
} from "./ConceptExplorer";
import {
  ExplorerContextMenu,
  type ExplorerMenuItem,
  type ExplorerMenuStand,
} from "./ExplorerContextMenu";
import { MoveToFolderDialog } from "./MoveToFolderDialog";
import { RelationGlyph } from "./RelationGlyph";

const SIGNAL_COLOR_RED = "#dc2626";

/** Sharp-types die als child onder hun eigenaar-Concept worden genest i.p.v.
 *  als eigen type-groep getoond. */
const NESTED_CHILD_TYPES = new Set(["sharp.Attribute", "sharp.Identifier"]);

/**
 * Toon-naam van een Explorer-item. Voor `sharp.Attribute` / `sharp.Identifier`
 * leiden we de naam live af van een eventueel gekoppeld `sharp.AttributeType`
 * (via de store, niet de — mogelijk stale — tree-DTO-naam), zodat het wisselen
 * van attribuuttype direct doorkomt. Geen concept-prefix meer: ze hangen genest
 * onder hun Concept. Voor alle andere items de gewone bilinguale naam.
 */
function explorerItemName(
  item: { id: string; typeId: string; name: Record<string, string> },
  lang: string,
): string {
  if (!NESTED_CHILD_TYPES.has(item.typeId)) {
    return resolveBilingual(item.name, lang);
  }
  const store = useWorkspaceData.getState().store;
  const self = store.getConcept(item.id);
  return self
    ? resolveConceptName(self, (id) => store.getConcept(id), lang)
    : resolveBilingual(item.name, lang);
}
const STEREOTYPE_LAYER_LABELS: Record<string, { en: string; nl: string }> = {
  archimate: { en: "ArchiMate", nl: "ArchiMate" },
  c4: { en: "C4", nl: "C4" },
  sharp: { en: "Concept modelling", nl: "Concept modelling" },
  bmc: { en: "BMC", nl: "BMC" },
  risk: { en: "Risk & Security", nl: "Risico en beveiliging" },
  roadmap: { en: "I&M (Roadmap)", nl: "I&M (Roadmap)" },
  drawing: { en: "Drawing", nl: "Tekenen" },
};

/** Vlatte lijst van alle item-IDs in tree-volgorde voor Shift+klik bereik-selectie. */
function flattenTreeIds(tree: ExplorerTreeDTO | null): string[] {
  if (!tree) return [];
  const ids: string[] = [];
  for (const layer of tree.layers) {
    for (const g of layer.objectGroups) for (const it of g.items) ids.push(it.id);
    for (const g of layer.diagramGroups) for (const it of g.items) ids.push(it.id);
  }
  return ids;
}

export function Explorer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const open = useUi((s) => s.explorerOpen);
  const setOpen = useUi((s) => s.setExplorerOpen);
  const explorerReveal = useUi((s) => s.explorerReveal);
  const openGlossary = useUi((s) => s.openGlossary);
  const pinned = useUi((s) => s.explorerPinned);
  const setPinned = useUi((s) => s.setExplorerPinned);
  const { width: panelWidth, ResizeHandle } = usePanelWidth(
    "ae.panelWidth.m6Explorer",
    320,
    "right",
  );
  const workspaceId = useSession((s) => s.workspaceId);
  // Alleen kijken: bladeren, selecteren en openen mag; slepen, verplaatsen,
  // wissen en een nieuwe branch niet.
  const readOnly = useSession((s) => s.readOnly);
  // Admins zien de Base-WP in de selector zodat ze folder-content direct
  // op het canvas kunnen modelleren (M6.5-W2 originele restrictie was
  // "modelleur kan Base niet kiezen"; admins zijn de modelleurs van Base
  // zelf). Non-admins blijven Base uit de keuze houden — folder-objecten
  // beheren ze via subscriber-WPs met read/write-access.
  const isAdmin = useSession((s) => isAdminOfWorkspace(s.me, s.workspaceId));
  // openDiagram = setActiveDiagram + tab-bar-add. Gebruik dit overal
  // waar de Explorer een diagram opent zodat 't ook bovenaan als tab
  // verschijnt; setActiveDiagram alleen wisselt actief zonder de tab
  // toe te voegen, wat verwarrend is bij Explorer-jumps.
  const openDiagram = useWorkspaceData((s) => s.openDiagram);
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);

  const [workpackages, setWorkpackages] = useState<WorkPackageDTO[]>([]);
  // Init via useSession zodat een hard refresh teruggaat naar het
  // laatst-gekozen workpackage. setSelectedWpId synced beneden terug
  // naar useSession.setWorkpackageId zodat localStorage geüpdatet wordt.
  const sessionWpId = useSession((s) => s.workpackageId);
  const [selectedWpId, setSelectedWpId] = useState<string | null>(sessionWpId);
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  // "+ Nieuwe branch"-modal — opent via de sentinel-optie onderaan de
  // branch-dropdown. Na creatie wordt de nieuwe branch direct geselecteerd
  // (en gesynced naar useSession zodat canvas + PATCH-paden meegaan).
  const [newBranchModalOpen, setNewBranchModalOpen] = useState(false);
  const [tree, setTree] = useState<ExplorerTreeDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Multi-select state: anchor (laatst geklikte item) + selected-set
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [moveToFolderOpen, setMoveToFolderOpen] = useState(false);
  const [detail, setDetail] = useState<ObjectDetailDTO | null>(null);
  const [diagramDetail, setDiagramDetail] = useState<DiagramDetailDTO | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Expliciete uitvouw-state: detail wordt alleen getoond wanneer de
  // gebruiker op het +-knopje achter een naam klikt. Onafhankelijk van
  // de bestaande selectie zodat selecteren een lichtgewicht actie blijft
  // (canvas-highlight, drag-source) en het detail-paneel een opt-in is.
  const [detailExpandedId, setDetailExpandedId] = useState<string | null>(null);
  const [popupItem, setPopupItem] = useState<{
    id: string;
    name: BilingualText;
    diagrams: DiagramOccurrenceDTO[];
  } | null>(null);
  // Substring-zoekveld bovenaan — filtert objecten + diagrammen op
  // resolved-bilingual-naam (case-insensitive). Lege query = geen
  // filter. Layer-groepen + type-groepen zonder hits worden verborgen
  // zodat de visuele ruis daalt naarmate je typt.
  const [searchQuery, setSearchQuery] = useState("");
  // Welke laag-secties (ARCHIMATE / DATA MODELING / DRAWING / ...) zijn
  // ingeklapt? Gepersisteerd in localStorage zodat de gebruiker bij
  // een refresh weer dezelfde inkluk-staat ziet. Set met layer-keys
  // die DICHT staan; afwezig = open (default).
  const [collapsedLayers, setCollapsedLayers] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem("ae.explorer.collapsedLayers");
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((v): v is string => typeof v === "string"));
      }
    } catch {
      /* ignore corrupt cache */
    }
    return new Set();
  });
  const toggleLayerCollapsed = (layerKey: string) => {
    setCollapsedLayers((prev) => {
      const next = new Set(prev);
      if (next.has(layerKey)) next.delete(layerKey);
      else next.add(layerKey);
      try {
        window.localStorage.setItem(
          "ae.explorer.collapsedLayers",
          JSON.stringify(Array.from(next)),
        );
      } catch {
        /* ignore quota / disabled storage */
      }
      return next;
    });
  };

  // Klap alle lagen tegelijk in. De object-/diagram-subgroepen zijn native
  // <details> die alleen renderen zolang hun laag open is; door alle lagen
  // in te klappen mounten die subgroepen straks opnieuw (dus dicht) zodra een
  // laag weer opengaat — één klik geeft dus de volledig-compacte weergave.
  // We collapse-n álle tree.layers (niet de search-gefilterde subset) en
  // sluiten meteen het inline-detail.
  const collapseAllLayers = () => {
    const next = new Set<string>((tree?.layers ?? []).map((l) => l.layer));
    setCollapsedLayers(next);
    try {
      window.localStorage.setItem("ae.explorer.collapsedLayers", JSON.stringify(Array.from(next)));
    } catch {
      /* ignore quota / disabled storage */
    }
    setDetailExpandedId(null);
  };

  // Workpackages laden
  // Re-fetch-counter: WorkpackagesSettings dispatcht
  // `ae:workpackages-changed` na elke succesvolle CRUD; we bumpen
  // de counter zodat de fetch-useEffect opnieuw draait en de
  // dropdown direct synchroon is met Settings.
  const [wpRefreshCounter, setWpRefreshCounter] = useState(0);
  useEffect(() => {
    const onChanged = () => setWpRefreshCounter((c) => c + 1);
    window.addEventListener("ae:workpackages-changed", onChanged);
    return () => window.removeEventListener("ae:workpackages-changed", onChanged);
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    api
      .listWorkpackages(workspaceId)
      .then((r) => {
        if (cancelled) return;
        // Admins zien Base mee in de selector (om folder-content direct
        // op canvas te modelleren); non-admins krijgen 'm gefilterd.
        const modelable = isAdmin ? r.workpackages : r.workpackages.filter((wp) => !wp.isBase);
        setWorkpackages(modelable);
        // Valideer de gepersisteerde WP-id; als 'ie niet meer bestaat
        // (bv. verwijderd via Settings of een Base-WP geworden), val
        // terug op het eerste modelable WP.
        // Lees de sessie-WP vers: na een workspace-wissel blijft deze
        // component gemount en bevat selectedWpId nog het WP van de vórige
        // workspace, terwijl hydrate() het onthouden WP van de nieuwe al
        // in de sessie heeft gezet.
        const sessionWp = useSession.getState().workpackageId;
        const preferred = [sessionWp, selectedWpId].find(
          (id) => id && modelable.some((wp) => wp.id === id),
        );
        // Zonder (geldige) voorkeur: het eerste gewone WP in dropdown-
        // volgorde; Base alleen als er niets anders is. Anders kwam een
        // admin bij elke nieuwe workspace in Base terecht.
        const fallback =
          modelable
            .filter((wp) => !wp.isBase)
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))[0] ?? modelable[0];
        const target = preferred ?? fallback?.id;
        if (target && target !== selectedWpId) setSelectedWpId(target);
        if (target && target !== sessionWp) {
          // Sync óók naar useSession — anders blijft session.workpackageId
          // null terwijl de Explorer een WP toont, en WP-gescopede acties
          // (zoals de Repository-JSON-export) vallen terug op
          // workspace-wide (= alle WPs in de export i.p.v. alleen de
          // actieve). Bug 2026-05-29: AMEFF-export bevatte Development.
          useSession.getState().setWorkpackageId(target);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, wpRefreshCounter]);

  // Branches laden — initial selectie volgt useSession.branchId zodat
  // de Explorer-tree op dezelfde branch start als de canvas (en
  // localStorage-restore in useSession werkt door tot hier).
  const sessionBranchId = useSession((s) => s.branchId);
  useEffect(() => {
    if (!workspaceId || !selectedWpId) return;
    let cancelled = false;
    api
      .listWorkpackageBranches(workspaceId, selectedWpId)
      .then((r) => {
        if (cancelled) return;
        // Een samengevoegde branch bestaat alleen nog als geschiedenis
        // (branch-merge.md §3.5). Stond de sessie erop, dan valt de keuze
        // hieronder vanzelf terug op main.
        const workable = r.branches.filter((b) => b.status !== "merged");
        setBranches(workable);
        // Prefer de session-branch (= eventueel uit localStorage gerestoreerd);
        // anders main; anders eerste beschikbare.
        const sessionBranch = sessionBranchId
          ? workable.find((b) => b.id === sessionBranchId)
          : null;
        const main = workable.find((b) => b.is_main);
        const fallback = sessionBranch ?? main ?? workable[0];
        if (fallback) {
          setSelectedBranchId(fallback.id);
          // Als de gepersisteerde session-branch NIET bij deze WP hoort
          // (bv. na een WP-switch of refresh waar de localStorage-branch
          // bij een andere WP zit), sync de session-branch naar de
          // fallback. Anders blijft Editor.tsx op de oude branch hydraten
          // — modelleur ziet dan de tabs van de vorige WP nog open.
          if (!sessionBranch && fallback.id !== sessionBranchId) {
            useSession.getState().setBranchId(fallback.id);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, selectedWpId, sessionBranchId]);

  // Workspace-revision is een teller die de workspace-store bumpt bij
  // elke mutatie van concepts/relations/diagrams. Door 'm in de
  // dep-array van de tree-fetch op te nemen ververst de Explorer
  // automatisch zodra een gebruiker een object plaatst, een naam
  // wijzigt etc. — zonder F5. We debouncen de fetch met 400ms zodat
  // een burst van mutaties (drag, rapid edits) niet elke keystroke
  // een netwerk-call kost.
  // Uitgesteld: tijdens slepen/lijn trekken stijgt de revisie bij elke muisbeweging;
  // de Explorer (honderden rijen) mag daar achteraan lopen zonder het canvas te blokkeren.
  const workspaceRevision = useDeferredValue(useWorkspaceData((s) => s.revision));
  // Attribuut/Identifier-children per eigenaar-Concept-id (uit `parentId` op de
  // tree-items). Gesorteerd: identifiers eerst, daarna attributen, beide op
  // (live afgeleide) naam. De Sharp-laag rendert ze genest onder hun Concept.
  const attrChildrenByParent = useMemo(() => {
    void workspaceRevision;
    const map = new Map<string, ExplorerConceptItem[]>();
    for (const layer of tree?.layers ?? []) {
      for (const g of layer.objectGroups) {
        if (!NESTED_CHILD_TYPES.has(g.typeId)) continue;
        for (const it of g.items) {
          if (!it.parentId) continue;
          const arr = map.get(it.parentId) ?? [];
          if (!arr.some((x) => x.id === it.id)) arr.push(it);
          map.set(it.parentId, arr);
        }
      }
    }
    const nameOf = (it: ExplorerConceptItem) => explorerItemName(it, lang).toLocaleLowerCase();
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const ar = a.typeId === "sharp.Identifier" ? 0 : 1;
        const br = b.typeId === "sharp.Identifier" ? 0 : 1;
        if (ar !== br) return ar - br;
        return nameOf(a).localeCompare(nameOf(b), lang);
      });
    }
    return map;
  }, [tree, workspaceRevision, lang]);

  // Tree laden
  const treeKeyRef = useRef("");
  useEffect(() => {
    if (!workspaceId || !selectedWpId || !selectedBranchId) {
      treeKeyRef.current = "";
      setTree(null);
      setLoading(false);
      return;
    }
    // Een antwoord is pas verouderd als werkplek/workpackage/branch wisselde, NIET
    // bij elke revisie-update: anders annuleert een revisie-storm (bv. de live-
    // verbinding die steeds opnieuw probeert) de eerste load telkens en blijft de
    // Explorer eeuwig op "Laden…" staan.
    const key = `${workspaceId}|${selectedWpId}|${selectedBranchId}`;
    const keyChanged = treeKeyRef.current !== key;
    treeKeyRef.current = key;
    let timerCancelled = false;
    // Bij eerste load (tree nog null) laden we direct; bij revision-
    // updates debouncen we zodat een drag-storm niet elke 50ms een call
    // veroorzaakt.
    const isFirstLoad = tree === null || keyChanged;
    if (isFirstLoad) setLoading(true);
    const refetch = () => {
      if (timerCancelled) return;
      api
        .getExplorerTree(workspaceId, selectedWpId, selectedBranchId)
        .then((r) => {
          if (treeKeyRef.current !== key) return;
          setTree(r);
          setError(null);
        })
        .catch((err) => {
          if (treeKeyRef.current === key)
            setError(err instanceof ApiError ? err.message : String(err));
        })
        .finally(() => {
          if (treeKeyRef.current === key) setLoading(false);
        });
    };
    if (isFirstLoad) {
      refetch();
    } else {
      const timer = setTimeout(refetch, 400);
      return () => {
        timerCancelled = true;
        clearTimeout(timer);
      };
    }
    return () => {
      timerCancelled = true;
    };
  }, [workspaceId, selectedWpId, selectedBranchId, workspaceRevision]);

  // Detail laden zodra de gebruiker een item uitvouwt via het +-knopje.
  // Klassificeer concept-vs-diagram door in de tree te kijken — scheelt
  // een 404-rondreis.
  useEffect(() => {
    if (!workspaceId || !detailExpandedId || !tree) {
      setDetail(null);
      setDiagramDetail(null);
      return;
    }
    let isDiagram = false;
    for (const layer of tree.layers) {
      for (const g of layer.diagramGroups) {
        if (g.items.some((d) => d.id === detailExpandedId)) {
          isDiagram = true;
          break;
        }
      }
      if (isDiagram) break;
    }
    let cancelled = false;
    setDetailLoading(true);
    if (isDiagram) {
      setDetail(null);
      api
        .getDiagramDetail(workspaceId, detailExpandedId)
        .then((r) => {
          if (!cancelled) setDiagramDetail(r);
        })
        .catch(() => {
          if (!cancelled) setDiagramDetail(null);
        })
        .finally(() => {
          if (!cancelled) setDetailLoading(false);
        });
    } else {
      setDiagramDetail(null);
      api
        .getObjectDetail(workspaceId, detailExpandedId, selectedBranchId ?? undefined)
        .then((r) => {
          if (!cancelled) setDetail(r);
        })
        .catch(() => {
          if (!cancelled) setDetail(null);
        })
        .finally(() => {
          if (!cancelled) setDetailLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [workspaceId, detailExpandedId, tree, selectedBranchId]);

  const flatIds = useMemo(() => flattenTreeIds(tree), [tree]);

  // Workpackage-dropdown order: Base altijd bovenaan, rest alfabetisch.
  const sortedWorkpackages = useMemo(() => {
    const base = workpackages.filter((wp) => wp.isBase);
    const rest = workpackages
      .filter((wp) => !wp.isBase)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...base, ...rest];
  }, [workpackages]);

  // Branch-dropdown order: main-rol-branches altijd eerst (op alfabet),
  // rest daarna (op alfabet). Vanaf M6.5 kan een workspace meerdere main-
  // branches hebben (één per workpackage).
  const sortedBranches = useMemo(() => {
    const mains = branches
      .filter((b) => b.is_main)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
    const rest = branches
      .filter((b) => !b.is_main)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...mains, ...rest];
  }, [branches]);

  // Main-branch-id voor de groen-bolletje-indicator: een record met
  // `branch_id` gelijk aan deze id krijgt geen bolletje; alle andere wel.
  // Voor nu pakken we de eerste main-rol-branch (= main van Default-WP).
  // Een nettere implementatie zou een Set<branchId> zijn als de Explorer
  // WP-aware wordt — TODO M6.5-W2.
  const mainBranchId = useMemo(() => branches.find((b) => b.is_main)?.id ?? null, [branches]);
  const branchNameById = useMemo(() => {
    const map = new Map(branches.map((b) => [b.id, b.name] as const));
    return (id: string) => map.get(id);
  }, [branches]);

  // Aside-ref voor scoped keyboard-handler. We forceren NIET de focus
  // bij elke klik (dat brak inputs in de AttributePanel: bij Explorer-
  // initiated panel-open kreeg de input geen focus retention bij
  // typen). Een document-level listener leest in plaats daarvan
  // `document.activeElement` en checkt of 't binnen de aside zit.
  const asideRef = useRef<HTMLDivElement | null>(null);

  // "Toon in Explorer" (canvas-contextmenu): klap de omvattende laag uit,
  // open de type-groep, scroll ernaartoe en selecteer de regel. Puur visueel
  // + app-eigen selectie — geen keyboard-focus-steal, geen keymap-wijziging.
  // Een `nonce` (uit de ui-state) zorgt dat herhaalde verzoeken opnieuw vuren.
  const lastRevealNonce = useRef(0);
  useEffect(() => {
    if (!explorerReveal || explorerReveal.nonce === lastRevealNonce.current) return;
    lastRevealNonce.current = explorerReveal.nonce;
    const conceptId = explorerReveal.conceptId;
    // Actief zoekfilter zou het item kunnen verbergen → wissen.
    setSearchQuery("");
    setSelectedIds(new Set([conceptId]));
    // Laag die het concept bevat uitklappen (React-state conditional render).
    const layerKey = tree?.layers.find((l) =>
      l.objectGroups.some((g) => g.items.some((i) => i.id === conceptId)),
    )?.layer;
    if (layerKey) {
      setCollapsedLayers((prev) => {
        if (!prev.has(layerKey)) return prev;
        const next = new Set(prev);
        next.delete(layerKey);
        return next;
      });
    }
    // Na re-render: type-groep(en) openen + in beeld scrollen.
    const timer = window.setTimeout(() => {
      const el = asideRef.current?.querySelector<HTMLElement>(
        `[data-explorer-concept="${conceptId}"]`,
      );
      if (!el) return;
      let node: HTMLElement | null = el.parentElement;
      while (node && node !== asideRef.current) {
        if (node.tagName === "DETAILS") (node as HTMLDetailsElement).open = true;
        node = node.parentElement;
      }
      requestAnimationFrame(() => el.scrollIntoView({ block: "center", behavior: "smooth" }));
    }, 60);
    return () => window.clearTimeout(timer);
  }, [explorerReveal, tree]);

  // Houd de Explorer-selectie in sync met het actieve diagram: wanneer een
  // diagram wordt geopend (tab-klik, Explorer-jump, of programmatisch zoals de
  // vereenvoudigde-kopie-flow) verplaatst de tree-highlight mee. Zonder dit
  // bleef de highlight op het laatst-geklikte item staan (bv. het bron-diagram
  // na een "genereer vereenvoudigde kopie"). Alleen op een échte wijziging van
  // activeDiagramId zodat een concept-multiselectie niet spontaan wordt gewist.
  const lastActiveDiagramId = useRef<string | null>(null);
  useEffect(() => {
    if (activeDiagramId === lastActiveDiagramId.current) return;
    lastActiveDiagramId.current = activeDiagramId;
    if (!activeDiagramId) return;
    setSelectedIds(new Set([activeDiagramId]));
    setAnchorId(activeDiagramId);
  }, [activeDiagramId]);

  const handleClick = (id: string, e: React.MouseEvent) => {
    if (e.shiftKey && anchorId) {
      // Shift-klik: bereik tussen anchor en huidige
      const a = flatIds.indexOf(anchorId);
      const b = flatIds.indexOf(id);
      if (a >= 0 && b >= 0) {
        const [from, to] = a < b ? [a, b] : [b, a];
        setSelectedIds(new Set(flatIds.slice(from, to + 1)));
        return;
      }
    }
    if (e.metaKey || e.ctrlKey) {
      // Cmd/Ctrl-klik: toggle
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      setSelectedIds(next);
      setAnchorId(id);
      return;
    }
    // Normale klik: enkel-select
    setSelectedIds(new Set([id]));
    setAnchorId(id);
  };

  /** Refresh helper na een mutatie (bv. delete). */
  const refreshTree = () => {
    if (!workspaceId || !selectedWpId || !selectedBranchId) return;
    api
      .getExplorerTree(workspaceId, selectedWpId, selectedBranchId)
      .then((r) => setTree(r))
      .catch(() => {});
  };

  /** Document-level Delete / Backspace handler: verwijdert het
   *  geselecteerde tree-item (concept of diagram). We luisteren niet
   *  meer op de aside zelf — focus stelen brak inputs in andere
   *  panelen. In plaats daarvan checkt deze handler of een element
   *  binnen de Explorer-aside focus heeft (de tree-row of een button
   *  daarbinnen) zodat we niet vuren tijdens type-acties in de
   *  AttributePanel of canvas. */
  /** Bulk-delete-helper: gebruikt zowel door de Delete/Backspace-keydown
   *  als door de bulk-delete-toolbar-knop. Splitst de huidige selectie
   *  in concepts vs diagrams en doet één gecombineerde bevestiging
   *  (concept-edges via confirmHardDelete; aparte window.confirm voor
   *  diagrams). */
  const bulkDeleteSelection = useCallback(async () => {
    if (!workspaceId || !selectedBranchId || !tree) return;
    if (selectedIds.size === 0) return;
    if (useSession.getState().readOnly) return;
    // Splits selectie in concept-ids en diagram-ids op basis van de
    // tree-index.
    const conceptIds: string[] = [];
    const diagramIds: string[] = [];
    for (const id of selectedIds) {
      let foundAsDiagram = false;
      let foundAsConcept = false;
      for (const layer of tree.layers) {
        if (layer.diagramGroups.some((g) => g.items.some((i) => i.id === id))) {
          foundAsDiagram = true;
          break;
        }
        if (layer.objectGroups.some((g) => g.items.some((i) => i.id === id))) {
          foundAsConcept = true;
          break;
        }
      }
      if (foundAsDiagram) diagramIds.push(id);
      else if (foundAsConcept) conceptIds.push(id);
      // Anders: id zit niet (meer) in de huidige tree — bv. een stale/cross-
      // branch selectie of een attribuut (dat nu in het detail-paneel hangt,
      // niet als tree-rij). Skippen i.p.v. blind als concept verwijderen,
      // anders krijg je een "Concept '<vreemd-id>'"-bevestiging + preview-fail.
    }
    try {
      if (conceptIds.length > 0) {
        const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
        const ok = await confirmHardDelete({
          workspaceId,
          branchId: selectedBranchId,
          conceptIds,
          lang: langKey,
          standaloneEdgeCount: 0,
          conceptName: (id) => useWorkspaceData.getState().store.getConcept(id)?.name,
        });
        if (!ok) return;
        // Eén gedeelde action-id voor alle concepten in deze
        // bulk-delete zodat de Prullenbak ze als 1 entry toont.
        const actionId = crypto.randomUUID();
        for (const id of conceptIds) {
          await useWorkspaceData
            .getState()
            .removeConcept(workspaceId, selectedBranchId, id, actionId);
        }
      }
      if (diagramIds.length > 0) {
        const msg = t("explorer.bulkDelete.confirmDiagrams", { count: diagramIds.length });
        if (
          !(await confirmDialog({
            message: msg,
            danger: true,
          }))
        )
          return;
        for (const id of diagramIds) {
          await useWorkspaceData.getState().deleteDiagram(workspaceId, selectedBranchId, id);
        }
      }
      setSelectedIds(new Set());
      setAnchorId(null);
      setDetail(null);
      setDiagramDetail(null);
      refreshTree();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("bulk-delete failed", err);
      window.alert(t("explorer.deleteFailed"));
    }
  }, [selectedIds, workspaceId, selectedBranchId, tree, lang, refreshTree, t]);

  useEffect(() => {
    const onKeyDown = async (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      if (selectedIds.size === 0) return;
      // Alleen als de gebruiker in de Explorer werkt. De selectie volgt het
      // actieve diagram (zie hierboven), dus anders bood een Delete op het
      // canvas — of met de Explorer dicht — dat diagram ter verwijdering aan.
      const active = document.activeElement;
      if (
        !explorerOwnsDeleteKey({
          open,
          scope: useKeymap.getState().scope,
          focusInside: !!active && !!asideRef.current?.contains(active),
        })
      ) {
        return;
      }
      // Skip bij type-actions: input, textarea, select, of een
      // contenteditable element heeft focus → Delete/Backspace betekent
      // tekst-edit, niet item-verwijderen.
      if (isEditableTarget(active)) return;
      e.preventDefault();
      await bulkDeleteSelection();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, selectedIds, bulkDeleteSelection]);

  const openInDiagramPopup = (item: ExplorerConceptItem) => {
    if (!workspaceId) return;
    api
      .getObjectDiagrams(workspaceId, item.id, selectedBranchId ?? undefined)
      .then((r) => {
        setPopupItem({ id: item.id, name: item.name, diagrams: r.diagrams });
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };

  // Rechtermuismenu: alleen wat voor dít item en deze gebruiker kan.
  const [menu, setMenu] = useState<ExplorerMenuStand | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  const deleteConceptFromModel = async (item: ExplorerConceptItem) => {
    if (!workspaceId || !selectedBranchId || useSession.getState().readOnly) return;
    const ok = await confirmHardDelete({
      workspaceId,
      branchId: selectedBranchId,
      conceptIds: [item.id],
      lang: lang === "en" ? "en" : "nl",
      standaloneEdgeCount: 0,
      conceptName: () => item.name,
    });
    if (!ok) return;
    await useWorkspaceData
      .getState()
      .removeConcept(workspaceId, selectedBranchId, item.id, crypto.randomUUID());
  };

  const conceptMenu = (item: ExplorerConceptItem, e: React.MouseEvent) => {
    e.preventDefault();
    const items: ExplorerMenuItem[] = [
      {
        key: "open",
        label: t("explorer.contextMenu.openInDiagram"),
        onSelect: () => openInDiagramPopup(item),
      },
      {
        key: "properties",
        label: t("explorer.contextMenu.properties"),
        onSelect: () => {
          const ui = useUi.getState();
          ui.selectConcept(item.id);
          ui.setAttributePanelOpen(true);
        },
      },
    ];
    if (!readOnly && !item.tombstoneInBranch) {
      items.push({
        key: "delete",
        label: t("explorer.contextMenu.deleteFromModel"),
        danger: true,
        onSelect: () => void deleteConceptFromModel(item),
      });
    }
    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  const diagramMenu = (d: ExplorerDiagramItem, e: React.MouseEvent) => {
    e.preventDefault();
    if (!workspaceId) return;
    const items: ExplorerMenuItem[] = [
      {
        key: "open",
        label: t("explorer.contextMenu.open"),
        onSelect: () => openDiagram(workspaceId, d.id),
      },
      {
        key: "properties",
        label: t("explorer.contextMenu.properties"),
        onSelect: () => {
          const ui = useUi.getState();
          ui.selectDiagram(d.id);
          ui.setAttributePanelOpen(true);
        },
      },
    ];
    if (!readOnly) {
      items.splice(1, 0, {
        key: "rename",
        label: t("explorer.contextMenu.rename"),
        onSelect: () => {
          // Hernoemen gebeurt in de tab, net als F2: eerst openen, dan de
          // tab laten weten dat hij moet hernoemen (zodra hij er staat).
          openDiagram(workspaceId, d.id);
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("ae:rename-diagram", { detail: { diagramId: d.id } }),
            );
          }, 50);
        },
      });
      // Het laatste diagram kan niet weg (zie `deleteDiagram`).
      if (useWorkspaceData.getState().store.listDiagrams().length > 1 && selectedBranchId) {
        items.push({
          key: "delete",
          label: t("explorer.contextMenu.deleteDiagram"),
          danger: true,
          onSelect: async () => {
            if (
              !(await confirmDialog({
                message: t("explorer.bulkDelete.confirmDiagrams", { count: 1 }),
                danger: true,
              }))
            )
              return;
            void useWorkspaceData.getState().deleteDiagram(workspaceId, selectedBranchId, d.id);
          },
        });
      }
    }
    setMenu({ x: e.clientX, y: e.clientY, items });
  };

  const jumpToDiagram = (occurrence: DiagramOccurrenceDTO) => {
    if (workspaceId) openDiagram(workspaceId, occurrence.diagramId);
    setPopupItem(null);
  };

  const layerLabel = useMemo(
    () => (key: string) => STEREOTYPE_LAYER_LABELS[key]?.[lang === "nl" ? "nl" : "en"] ?? key,
    [lang],
  );

  if (!open) return null;

  return (
    <aside
      ref={asideRef as React.RefObject<HTMLElement>}
      data-scope="explorer"
      // De data-scope-active focus-ring rendert als `::after` met
      // `position: absolute; inset: 0` op dit element (zie globals.css).
      // Daarom mag DIT element NIET zelf scrollen — anders scrollt de
      // ring weg met de content bij uitgeklapte tree-groepen. We zetten
      // de overflow-y-auto op een inner wrapper (zie hieronder) zodat
      // de aside-bounds stabiel blijven en de ring bij elke expand/
      // collapse de visuele kader van het paneel matcht.
      className="ledger relative flex h-full shrink-0 flex-col overflow-hidden border-(--ld-control) border-r bg-(--ld-raised)"
      style={{ width: panelWidth }}
    >
      <header className="flex items-center justify-between border-(--ld-border) border-b bg-(--ld-surface) px-3 py-2">
        <h2 className="font-semibold text-(--ld-text) text-sm">{t("app.panels.explorer")}</h2>
        <div className="flex items-center gap-1">
          <GlossaryInfoButton
            termIds={[
              "workpackage",
              "branch",
              "concept",
              "stereotype",
              "attribute",
              "identifier",
              "attribute_type",
              "subject_area",
              "concept_constraint",
              "reification",
              "plateau",
            ]}
          />
          <button
            type="button"
            className={`rounded px-1 text-sm leading-none ${
              pinned
                ? "text-(--ld-accent) hover:text-(--ld-accent)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setPinned(!pinned)}
            title={pinned ? t("explorer.pinned") : t("explorer.pinPanel")}
            aria-pressed={pinned}
          >
            {pinned ? "📌" : "📍"}
          </button>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) text-base leading-none hover:bg-(--ld-active)"
            onClick={() => setOpen(false)}
            title={t("settings.common.close")}
          >
            <PanelCollapseIcon />
          </button>
        </div>
      </header>

      {/*
        Inner scroll-wrapper — neemt de overflow-y-auto van de aside
        over zodat de focus-ring (::after op aside) niet meeschuift met
        de tree-content. `min-h-0` is verplicht binnen `flex flex-col`
        anders kan flex-1 niet correct krimpen onder de min-content-
        hoogte van de children → overflow-y treedt niet in werking.
      */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-2 border-(--ld-border) border-b bg-(--ld-surface) px-3 py-2">
          <label className="flex flex-col gap-1 text-(--ld-muted) text-xs">
            <span className="font-medium">{t("explorer.workpackageLabel")}</span>
            <select
              className="h-8 rounded-md border border-(--ld-muted) bg-(--ld-raised) px-2 font-medium text-(--ld-text) text-sm hover:border-(--ld-text) focus:border-(--ld-accent) focus:outline-none"
              value={selectedWpId ?? ""}
              onChange={(e) => {
                const next = e.target.value || null;
                setSelectedWpId(next);
                // Persist naar useSession zodat een hard refresh dezelfde
                // WP terugbrengt. Branch wordt onafhankelijk gerestoreerd
                // via de bestaande useSession.branchId-keten.
                if (next) {
                  useSession.getState().setWorkpackageId(next);
                }
              }}
            >
              <option value="">{t("explorer.choose")}</option>
              {sortedWorkpackages.map((wp) => (
                <option key={wp.id} value={wp.id}>
                  {wp.name}
                  {wp.status === "closed" ? t("explorer.closedSuffix") : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-(--ld-muted) text-xs">
            <span className="font-medium">{t("explorer.branchLabel")}</span>
            <select
              className="h-8 rounded-md border border-(--ld-muted) bg-(--ld-raised) px-2 font-medium text-(--ld-text) text-sm hover:border-(--ld-text) focus:border-(--ld-accent) focus:outline-none"
              value={selectedBranchId ?? ""}
              onChange={(e) => {
                const next = e.target.value || null;
                if (next === "__new__") {
                  if (useSession.getState().readOnly) return;
                  setNewBranchModalOpen(true);
                  return;
                }
                setSelectedBranchId(next);
                // Sync ook de session-branch zodat het canvas + alle
                // PATCH-paden (incl. copy-on-write voor diagrams) vanaf
                // nu deze branch als active branch beschouwen. Anders
                // blijven mutaties op main landen ondanks de UI-keuze.
                if (next) {
                  useSession.getState().setBranchId(next);
                }
              }}
              disabled={branches.length === 0 && !selectedWpId}
            >
              <option value="">{t("explorer.choose")}</option>
              {sortedBranches.map((b) => (
                <option key={b.id} value={b.id}>
                  {displayBranchName(b, lang === "en" ? "en" : "nl")}
                </option>
              ))}
              {selectedWpId && !readOnly && (
                <option value="__new__">{t("explorer.newBranchOption")}</option>
              )}
            </select>
          </label>
        </div>

        <div className="border-(--ld-border) border-b bg-(--ld-surface) px-3 py-2">
          <div className="relative">
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("explorer.search.placeholder")}
              className="h-8 w-full rounded-md border border-(--ld-muted) bg-(--ld-raised) px-2 pr-6 text-(--ld-text) text-sm placeholder:text-(--ld-muted) hover:border-(--ld-text) focus:border-(--ld-accent) focus:outline-none"
              aria-label={t("explorer.search.ariaLabel")}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="-translate-y-1/2 absolute top-1/2 right-1.5 text-(--ld-muted) text-sm hover:text-(--ld-text)"
                title={t("explorer.search.clear")}
                aria-label={t("explorer.search.clearAria")}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/*
        Selectie-toolbar — wordt ALTIJD gerenderd zodat de tree-positie
        stabiel blijft. Bij geen selectie staat 'ie op `invisible`
        (= CSS `visibility: hidden`) wat 'm onzichtbaar maakt maar zijn
        verticale ruimte behoudt. Voorheen condition-rendering met `&&`
        liet de tree omhoog springen zodra een selectie verviel, waardoor
        dubbelklikken op een tree-item het verkeerde object aanklikte
        omdat de tweede klik op een verschoven rij landde.
      */}
        <div
          className={`flex items-center gap-2 border-(--ld-border) border-b px-3 py-1.5 text-xs ${
            selectedIds.size > 0 ? "bg-(--ld-active)" : "bg-(--ld-surface)"
          }`}
        >
          <button
            type="button"
            onClick={collapseAllLayers}
            className="flex items-center justify-center rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 text-(--ld-muted) hover:bg-(--ld-raised) hover:text-(--ld-text)"
            title={t("explorer.collapseAll")}
            aria-label={t("explorer.collapseAll")}
          >
            <span className="text-base leading-none">▴</span>
          </button>
          <div
            className={`ml-auto flex items-center gap-2 ${selectedIds.size > 0 ? "" : "invisible"}`}
            aria-hidden={selectedIds.size === 0}
          >
            <span className="text-(--ld-accent)">
              {t("explorer.selection.selectedCount", { n: selectedIds.size })}
            </span>
            <button
              type="button"
              onClick={() => setMoveToFolderOpen(true)}
              hidden={readOnly}
              className="flex items-center justify-center rounded border border-(--ld-accent) bg-(--ld-surface) px-1.5 py-0.5 text-(--ld-accent) hover:bg-(--ld-active)"
              title={t("explorer.selection.moveToFolder")}
              aria-label={t("explorer.selection.moveToFolder")}
              tabIndex={selectedIds.size > 0 ? 0 : -1}
            >
              <span className="text-base leading-none">📂</span>
            </button>
            <button
              type="button"
              onClick={() => void bulkDeleteSelection()}
              hidden={readOnly}
              className="flex items-center justify-center rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 text-(--ld-negative) hover:bg-(--ld-negative-bg)"
              title={t("explorer.selection.deleteTitle")}
              aria-label={t("settings.common.delete")}
              tabIndex={selectedIds.size > 0 ? 0 : -1}
            >
              <span className="text-base leading-none">🗑</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedIds(new Set());
                setAnchorId(null);
              }}
              className="rounded px-1 text-(--ld-muted) hover:text-(--ld-text)"
              title={t("foldertree.multiSelect.clearTitle")}
              tabIndex={selectedIds.size > 0 ? 0 : -1}
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-3 py-2">
          {error ? (
            <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-2 py-1 text-(--ld-negative) text-xs">
              {error}
            </p>
          ) : null}
          {loading ? (
            <p className="text-(--ld-muted) text-xs">{t("settings.common.loading")}</p>
          ) : !tree ? (
            <p className="text-(--ld-muted) text-xs">
              {workpackages.length === 0
                ? t("explorer.empty.noWorkpackages")
                : t("explorer.empty.pickWpBranch")}
            </p>
          ) : tree.layers.length === 0 ? (
            <p className="text-(--ld-muted) text-xs">{t("explorer.empty.branchEmpty")}</p>
          ) : (
            (() => {
              const q = searchQuery.trim().toLowerCase();
              // Drawing-laag wordt op gebruiker-keuze altijd verborgen in
              // de Explorer-tree: tekening-primitieven (rechthoek, lijn,
              // pijl, beschrijvings-box) zijn niet-semantisch en horen
              // niet als eersteklas concept-categorie in de boom. Filter
              // 'm zowel uit het zoek-resultaat als uit de basisweergave.
              // Sort items alfabetisch op resolveBilingual(name, lang) zodat
              // ze in de actieve UI-taal gesorteerd verschijnen — re-sorteert
              // automatisch bij taal-switch via React-render-cyclus.
              const sortByLang = <T extends { name: { nl?: string; en?: string } }>(
                items: readonly T[],
              ): T[] =>
                items
                  .slice()
                  .sort((a, b) =>
                    resolveBilingual(a.name, lang).localeCompare(
                      resolveBilingual(b.name, lang),
                      lang,
                    ),
                  );
              const visibleLayers = tree.layers
                .filter((l) => l.layer !== "drawing")
                .map((layer) => ({
                  ...layer,
                  objectGroups: layer.objectGroups.map((g) => ({
                    ...g,
                    // Sorteer op de getoonde naam — voor Attribute/Identifier is
                    // dat de samengestelde <conceptnaam>.<attribuutnaam>.
                    items: g.items
                      .slice()
                      .sort((a, b) =>
                        explorerItemName(a, lang).localeCompare(explorerItemName(b, lang), lang),
                      ),
                  })),
                  diagramGroups: layer.diagramGroups.map((g) => ({
                    ...g,
                    items: sortByLang(g.items),
                  })),
                }));
              const filteredLayers = q
                ? visibleLayers
                    .map((layer) => {
                      const objectGroups = layer.objectGroups
                        .map((g) => ({
                          ...g,
                          items: g.items.filter((it) =>
                            resolveBilingual(it.name, lang).toLowerCase().includes(q),
                          ),
                        }))
                        .filter((g) => g.items.length > 0)
                        .map((g) => ({ ...g, count: g.items.length }));
                      const diagramGroups = layer.diagramGroups
                        .map((g) => ({
                          ...g,
                          items: g.items.filter((it) =>
                            resolveBilingual(it.name, lang).toLowerCase().includes(q),
                          ),
                        }))
                        .filter((g) => g.items.length > 0)
                        .map((g) => ({ ...g, count: g.items.length }));
                      const total =
                        objectGroups.reduce((acc, g) => acc + g.items.length, 0) +
                        diagramGroups.reduce((acc, g) => acc + g.items.length, 0);
                      return { ...layer, objectGroups, diagramGroups, total };
                    })
                    .filter((layer) => layer.total > 0)
                : visibleLayers;
              if (q && filteredLayers.length === 0) {
                return (
                  <p className="text-(--ld-muted) text-xs">
                    {t("explorer.search.noResults", { query: searchQuery })}
                  </p>
                );
              }
              const layerSections = filteredLayers.map((layer) => {
                const isCollapsed = collapsedLayers.has(layer.layer);
                return (
                  <section key={layer.layer}>
                    <button
                      type="button"
                      onClick={() => toggleLayerCollapsed(layer.layer)}
                      className="-ml-0.5 mb-1 flex w-full items-center gap-1 rounded px-0.5 font-medium text-(--ld-muted) text-xs hover:bg-(--ld-raised) hover:text-(--ld-text)"
                      aria-expanded={!isCollapsed}
                    >
                      <span
                        aria-hidden
                        className="inline-block w-5 text-(--ld-muted) text-base leading-none"
                      >
                        {isCollapsed ? "▸" : "▾"}
                      </span>
                      <span>
                        {layerLabel(layer.layer)} ({layer.total})
                      </span>
                    </button>
                    {isCollapsed ? null : (
                      <ul className="ml-4 flex flex-col gap-1">
                        {layer.objectGroups.map((group) => {
                          // Attribuut/Identifier krijgen geen eigen type-groep:
                          // ze hangen genest onder hun eigenaar-Concept (zie de
                          // children-render onder elke concept-rij). Sla de groep
                          // hier dus over.
                          if (NESTED_CHILD_TYPES.has(group.typeId)) return null;
                          // Group-label resolven: voor stereotype-keys
                          // (bmc.CustomerSegment / c4.Container / sharp.Concept)
                          // gebruiken we het stereotype-label, voor element-
                          // type-keys het ElementType-label, met de raw id
                          // als laatste fallback.
                          const reg = getRegistry();
                          const stereo = reg.getStereotype(group.typeId);
                          const elType = stereo ? null : reg.getElementType(group.typeId);
                          const groupLabel = stereo
                            ? resolveBilingual(stereo.label, lang)
                            : elType
                              ? resolveBilingual(elType.label, lang)
                              : group.typeId;
                          return (
                            <li key={`obj:${group.typeId}`}>
                              <details className="group/objgroup">
                                <summary className="flex cursor-pointer list-none items-center gap-1 text-(--ld-text) text-xs hover:text-(--ld-text) [&::-webkit-details-marker]:hidden">
                                  <span
                                    aria-hidden
                                    className="inline-block w-5 text-(--ld-muted) text-base leading-none"
                                  >
                                    <span className="group-open/objgroup:hidden">▸</span>
                                    <span className="hidden group-open/objgroup:inline">▾</span>
                                  </span>
                                  {group.typeId === "sharp.AttributeType" ? (
                                    // Klik op het label → open de Glossary-view
                                    // (preventDefault stopt de details-toggle; de
                                    // chevron blijft in-/uitklappen).
                                    <button
                                      type="button"
                                      className="font-medium hover:text-(--ld-accent) hover:underline"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        openGlossary();
                                      }}
                                    >
                                      {groupLabel}
                                    </button>
                                  ) : (
                                    <span className="font-medium">{groupLabel}</span>
                                  )}{" "}
                                  <span className="text-(--ld-muted)">({group.count})</span>
                                </summary>
                                <ul className="ml-3 mt-0.5 flex flex-col gap-0.5 border-(--ld-border) border-l pl-2">
                                  {group.items.map((item) => {
                                    const isSelected = selectedIds.has(item.id);
                                    const isOrphan = item.signal === "orphan";
                                    const isExpanded = detailExpandedId === item.id;
                                    const showInlineDetail = isExpanded && detail;
                                    return (
                                      <li key={item.id}>
                                        <div
                                          tabIndex={0}
                                          role="treeitem"
                                          data-explorer-concept={item.id}
                                          className={`group flex cursor-pointer items-center justify-between rounded px-1 text-xs focus:bg-(--ld-active) focus:outline focus:outline-2 focus:outline-(--ld-accent) ${
                                            isSelected
                                              ? "bg-(--ld-active) text-(--ld-accent)"
                                              : "hover:bg-(--ld-raised)"
                                          } ${item.tombstoneInBranch ? "line-through" : ""}`}
                                          style={isOrphan ? { color: SIGNAL_COLOR_RED } : undefined}
                                          draggable={!item.tombstoneInBranch && !readOnly}
                                          onDragStart={(e) => {
                                            // Sleep een bestaand concept naar de canvas — gebruikt
                                            // dezelfde MIME-types als de oude ConceptExplorer zodat
                                            // Canvas.onDrop het kan oppakken (single of bulk).
                                            if (selectedIds.has(item.id) && selectedIds.size > 1) {
                                              e.dataTransfer.setData(
                                                EXISTING_CONCEPT_BULK_MIME,
                                                JSON.stringify(Array.from(selectedIds)),
                                              );
                                            } else {
                                              e.dataTransfer.setData(
                                                EXISTING_CONCEPT_MIME,
                                                item.id,
                                              );
                                            }
                                            e.dataTransfer.effectAllowed = "copy";
                                          }}
                                          onClick={(e) => handleClick(item.id, e)}
                                          onDoubleClick={() => {
                                            // Dubbelklik op een tree-item =
                                            // selecteer het concept én open
                                            // het AttributePanel (consistent
                                            // met de detail-tabel-rij-
                                            // dubbelklik verderop in dit
                                            // bestand).
                                            const ui = useUi.getState();
                                            ui.selectConcept(item.id);
                                            ui.setAttributePanelOpen(true);
                                          }}
                                          onKeyDown={(e) => {
                                            // Alleen reageren wanneer focus
                                            // op de item-div zelf zit, niet
                                            // op een ge-neste +/openin-diagram-
                                            // button — anders cancelen we
                                            // de native Enter→click op die
                                            // buttons via preventDefault.
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              handleClick(
                                                item.id,
                                                e as unknown as React.MouseEvent,
                                              );
                                              // Plus: open AttributePanel +
                                              // verplaats scope-focus daarheen
                                              // (zelfde patroon als ScopePicker.
                                              // commitChoice). Tab-trap uit
                                              // Editor.tsx cyclet daarna binnen
                                              // AttributePanel.
                                              const ui = useUi.getState();
                                              ui.selectConcept(item.id);
                                              ui.setAttributePanelOpen(true);
                                              useKeymap.getState().setScope("attribute-panel");
                                              setTimeout(() => {
                                                const scopeEl = document.querySelector<HTMLElement>(
                                                  '[data-scope="attribute-panel"]',
                                                );
                                                const first = scopeEl?.querySelector<HTMLElement>(
                                                  'button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
                                                );
                                                first?.focus();
                                              }, 0);
                                            }
                                          }}
                                          onContextMenu={(e) => conceptMenu(item, e)}
                                          title={
                                            isOrphan
                                              ? t("explorer.item.orphanTitle")
                                              : item.tombstoneInBranch
                                                ? t("explorer.item.tombstoneTitle")
                                                : t("explorer.item.conceptHint")
                                          }
                                        >
                                          <button
                                            type="button"
                                            className="inline-block w-5 shrink-0 text-(--ld-muted) text-base leading-none hover:text-(--ld-accent)"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setDetailExpandedId(isExpanded ? null : item.id);
                                            }}
                                            title={
                                              isExpanded
                                                ? t("explorer.item.hideDetail")
                                                : t("explorer.item.showDetail")
                                            }
                                            aria-expanded={isExpanded}
                                            aria-label={
                                              isExpanded
                                                ? t("explorer.item.collapseDetail")
                                                : t("explorer.item.expandDetail")
                                            }
                                          >
                                            {isExpanded ? "▾" : "▸"}
                                          </button>
                                          <span className="flex min-w-0 flex-1 items-center gap-2">
                                            <span className="truncate">
                                              {explorerItemName(item, lang)}
                                            </span>
                                            {item.inFolder ? (
                                              <span
                                                aria-hidden
                                                className="shrink-0 text-(--ld-notice-ink) text-xs"
                                                title={
                                                  item.folderName
                                                    ? t("explorer.item.fromFolder", {
                                                        folder: item.folderName,
                                                      })
                                                    : t("explorer.item.referenceFromBase")
                                                }
                                              >
                                                📁
                                              </span>
                                            ) : null}
                                            <BranchOwnershipDot
                                              recordBranchId={item.branchId}
                                              mainBranchId={mainBranchId}
                                              branchNameById={branchNameById}
                                            />
                                          </span>
                                          <button
                                            type="button"
                                            className="ml-1 text-(--ld-muted) opacity-0 transition-opacity hover:text-(--ld-accent) focus:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openInDiagramPopup(item);
                                            }}
                                            title={t("explorer.item.openInDiagram")}
                                          >
                                            🔗
                                          </button>
                                        </div>
                                        {showInlineDetail ? (
                                          <div className="my-1">
                                            <DetailPanel
                                              detail={detail}
                                              attrChildren={
                                                layer.layer === "sharp"
                                                  ? (attrChildrenByParent.get(item.id) ?? [])
                                                  : []
                                              }
                                              lang={lang}
                                              loading={detailLoading}
                                              branches={branches}
                                              workspaceId={workspaceId}
                                              branchId={selectedBranchId}
                                              onJumpToDiagram={(occ) => jumpToDiagram(occ)}
                                              onDeleted={() => {
                                                setSelectedIds(new Set());
                                                setAnchorId(null);
                                                setDetail(null);
                                                setDetailExpandedId(null);
                                                // Refetch tree
                                                if (
                                                  workspaceId &&
                                                  selectedWpId &&
                                                  selectedBranchId
                                                ) {
                                                  api
                                                    .getExplorerTree(
                                                      workspaceId,
                                                      selectedWpId,
                                                      selectedBranchId,
                                                    )
                                                    .then((r) => setTree(r))
                                                    .catch(() => {});
                                                }
                                              }}
                                            />
                                          </div>
                                        ) : null}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </details>
                            </li>
                          );
                        })}
                        {layer.diagramGroups.length > 0 ? (
                          <li>
                            <details className="group/diagrams">
                              <summary className="flex cursor-pointer list-none items-center gap-1 text-(--ld-text) text-xs hover:text-(--ld-text) [&::-webkit-details-marker]:hidden">
                                <span
                                  aria-hidden
                                  className="inline-block w-5 text-(--ld-muted) text-base leading-none"
                                >
                                  <span className="group-open/diagrams:hidden">▸</span>
                                  <span className="hidden group-open/diagrams:inline">▾</span>
                                </span>
                                <span className="font-medium">{t("explorer.diagramsGroup")}</span>{" "}
                                <span className="text-(--ld-muted)">
                                  ({layer.diagramGroups.reduce((acc, g) => acc + g.count, 0)})
                                </span>
                              </summary>
                              <ul className="ml-3 mt-0.5 flex flex-col gap-0.5 border-(--ld-border) border-l pl-2">
                                {/* M6.5 — diagrammen worden niet meer
                                 *  gegroepeerd per viewKind; vlakke lijst
                                 *  binnen [Diagrams]. Type-info verschuift
                                 *  naar het detail-panel bij uitklap. */}
                                {layer.diagramGroups
                                  .flatMap((group) => group.items)
                                  .map((d) => {
                                    const isSelected = selectedIds.has(d.id);
                                    const isEmpty = d.signal === "empty_diagram";
                                    const isExpanded = detailExpandedId === d.id;
                                    const showInlineDetail = isExpanded && diagramDetail;
                                    return (
                                      <li key={d.id}>
                                        <div
                                          tabIndex={0}
                                          role="treeitem"
                                          className={`group flex cursor-pointer items-center justify-between rounded px-1 text-xs focus:bg-(--ld-active) focus:outline focus:outline-2 focus:outline-(--ld-accent) ${
                                            isSelected
                                              ? "bg-(--ld-active) text-(--ld-accent)"
                                              : "hover:bg-(--ld-raised)"
                                          }`}
                                          style={isEmpty ? { color: SIGNAL_COLOR_RED } : undefined}
                                          draggable={!readOnly}
                                          onDragStart={(e) => {
                                            // Diagram-drag voor drawing.ListBox (en
                                            // toekomstige diagram-drop-handlers).
                                            // Eigen MIME zodat Canvas / ListBox het
                                            // type kunnen onderscheiden van een
                                            // concept-drag.
                                            e.dataTransfer.setData(EXISTING_DIAGRAM_MIME, d.id);
                                            e.dataTransfer.effectAllowed = "copy";
                                          }}
                                          onClick={(e) => handleClick(d.id, e)}
                                          onKeyDown={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              if (e.shiftKey || e.metaKey || e.ctrlKey) {
                                                handleClick(d.id, e as unknown as React.MouseEvent);
                                              } else if (workspaceId) {
                                                openDiagram(workspaceId, d.id);
                                              }
                                            }
                                          }}
                                          onDoubleClick={() => {
                                            if (workspaceId) openDiagram(workspaceId, d.id);
                                          }}
                                          onContextMenu={(e) => diagramMenu(d, e)}
                                          title={
                                            isEmpty
                                              ? t("explorer.item.emptyDiagramTitle")
                                              : t("explorer.item.diagramHint")
                                          }
                                        >
                                          <button
                                            type="button"
                                            className="inline-block w-5 shrink-0 text-(--ld-muted) text-base leading-none hover:text-(--ld-accent)"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setDetailExpandedId(isExpanded ? null : d.id);
                                            }}
                                            title={
                                              isExpanded
                                                ? t("explorer.item.hideDetail")
                                                : t("explorer.item.showDetail")
                                            }
                                            aria-expanded={isExpanded}
                                            aria-label={
                                              isExpanded
                                                ? t("explorer.item.collapseDetail")
                                                : t("explorer.item.expandDetail")
                                            }
                                          >
                                            {isExpanded ? "▾" : "▸"}
                                          </button>
                                          <span className="flex min-w-0 flex-1 items-center gap-2">
                                            <span className="truncate">
                                              {resolveBilingual(d.name, lang)}
                                            </span>
                                            <BranchOwnershipDot
                                              recordBranchId={d.branchId}
                                              mainBranchId={mainBranchId}
                                              branchNameById={branchNameById}
                                            />
                                          </span>
                                        </div>
                                        {showInlineDetail ? (
                                          <div className="my-1">
                                            <DiagramDetailPanel
                                              detail={diagramDetail}
                                              lang={lang}
                                              loading={detailLoading}
                                              branches={branches}
                                              workspaceId={workspaceId}
                                              branchId={selectedBranchId}
                                              onSelectObject={(id) => {
                                                setSelectedIds(new Set([id]));
                                                setAnchorId(id);
                                              }}
                                              onOpenDiagram={() => {
                                                if (workspaceId) {
                                                  openDiagram(
                                                    workspaceId,
                                                    diagramDetail.diagram.id,
                                                  );
                                                }
                                              }}
                                              onDeleted={() => {
                                                setSelectedIds(new Set());
                                                setAnchorId(null);
                                                setDiagramDetail(null);
                                                setDetailExpandedId(null);
                                                if (
                                                  workspaceId &&
                                                  selectedWpId &&
                                                  selectedBranchId
                                                ) {
                                                  api
                                                    .getExplorerTree(
                                                      workspaceId,
                                                      selectedWpId,
                                                      selectedBranchId,
                                                    )
                                                    .then((r) => setTree(r))
                                                    .catch(() => {});
                                                }
                                              }}
                                            />
                                          </div>
                                        ) : null}
                                      </li>
                                    );
                                  })}
                              </ul>
                            </details>
                          </li>
                        ) : null}
                      </ul>
                    )}
                  </section>
                );
              });
              return layerSections;
            })()
          )}

          {/* Selection summary */}
          {selectedIds.size > 1 ? (
            <div className="rounded border border-(--ld-border) bg-(--ld-surface) px-2 py-1 text-(--ld-muted) text-xs">
              {t("explorer.selection.summary", { n: selectedIds.size })}
            </div>
          ) : null}

          {/* Detail-paneel rendert nu inline onder het geselecteerde
            item in de tree zelf — zie de map() hierboven. */}
        </div>
      </div>
      {/* /Inner scroll-wrapper */}

      {menu ? <ExplorerContextMenu stand={menu} onClose={closeMenu} /> : null}

      {/* Open-in-diagram popup */}
      {popupItem ? (
        <OpenInDiagramPopup
          item={popupItem}
          lang={lang}
          onClose={() => setPopupItem(null)}
          onJump={(occ) => jumpToDiagram(occ)}
        />
      ) : null}

      {moveToFolderOpen && workspaceId && (
        <MoveToFolderDialog
          workspaceId={workspaceId}
          conceptIds={Array.from(selectedIds)}
          lang={lang}
          onClose={(refresh) => {
            setMoveToFolderOpen(false);
            if (refresh) {
              setSelectedIds(new Set());
              // Refetch tree in-place — `setTree(null)` zou een
              // "Laden…"-flikker geven en de focus uit Explorer
              // verwijderen.
              if (selectedWpId && selectedBranchId) {
                void api
                  .getExplorerTree(workspaceId, selectedWpId, selectedBranchId)
                  .then((r) => setTree(r))
                  .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
              }
            }
          }}
        />
      )}

      <ResizeHandle />
      {newBranchModalOpen && workspaceId && (
        <NewBranchModal
          workspaceId={workspaceId}
          workpackageId={selectedWpId}
          onClose={() => setNewBranchModalOpen(false)}
          onCreated={async (newBranchId) => {
            setNewBranchModalOpen(false);
            // Refresh de WP-branches en activeer de nieuwe branch.
            if (selectedWpId) {
              try {
                const r = await api.listWorkpackageBranches(workspaceId, selectedWpId);
                setBranches(r.branches.filter((b) => b.status !== "merged"));
              } catch {
                // Niet-fataal — de hydrate via dependency-keten zal de
                // tree-fetch herstellen.
              }
            }
            setSelectedBranchId(newBranchId);
            useSession.getState().setBranchId(newBranchId);
          }}
        />
      )}
    </aside>
  );
}

function DetailPanel({
  detail,
  attrChildren,
  lang,
  loading,
  branches,
  workspaceId,
  branchId,
  onJumpToDiagram,
  onDeleted,
}: {
  detail: ObjectDetailDTO;
  /** Sharp: attribuut/identifier-children van dit Concept (identifiers eerst,
   *  daarna attributen, op naam). Genest als "Attributen"-sectie. */
  attrChildren: ExplorerConceptItem[];
  lang: string;
  loading: boolean;
  branches: BranchSummary[];
  workspaceId: string | null;
  branchId: string | null;
  onJumpToDiagram: (occ: DiagramOccurrenceDTO) => void;
  onDeleted: () => void;
}) {
  const mainBranchId = branches.find((b) => b.is_main)?.id ?? null;
  const branchNameById = (id: string) => branches.find((b) => b.id === id)?.name;
  const { t } = useTranslation();
  // Alleen kijken: geen wissen en niet naar het canvas slepen.
  const readOnly = useSession((s) => s.readOnly);
  // BELANGRIJK: alle hooks MOETEN vóór de loading early-return staan,
  // anders schendt React's hook-ordering bij wisseling tussen
  // loading-states (crash "Rendered fewer hooks than expected").
  // Per-relatie expansion-state voor "Diagram-voorkomens"-sublijst.
  // Set i.p.v. enkele id zodat meerdere relaties tegelijk open kunnen
  // staan voor vergelijking. 2026-05-26.
  const [expandedRelationIds, setExpandedRelationIds] = useState<Set<string>>(() => new Set());
  if (loading) {
    return <p className="text-(--ld-muted) text-xs">{t("explorer.detail.loading")}</p>;
  }
  const concept = detail.concept as Concept;

  // Resolve registry-schema voor dit type — zelfde aanpak als
  // AttributePanel (de rechter sidebar): toon name + description +
  // alle schema-velden + stereotype-attributen, ook als ze leeg zijn.
  // Dat geeft dezelfde "wat-zou-er-kunnen-staan"-overview als de
  // bewerk-paneel rechts, alleen read-only.
  const reg = getRegistry();
  const elementType = reg.getElementType(concept.typeId);
  const store = useWorkspaceData.getState().store;
  const toggleRelationExpansion = (id: string) => {
    setExpandedRelationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Type-label in header-prioriteit: het meest specifieke C4/Sharp/BMC-
  // stereotype als 't aanwezig is (bv. «Person» of «Container» liever
  // dan "Business Actor" of "Application Component"), anders de
  // ArchiMate-base-type-label.
  const dominantStereotypeId = (concept.stereotypes ?? []).find(
    (s) => s.startsWith("c4.") || s.startsWith("sharp.") || s.startsWith("bmc."),
  );
  const dominantStereotype = dominantStereotypeId ? reg.getStereotype(dominantStereotypeId) : null;
  const typeLabel = dominantStereotype
    ? resolveBilingual(dominantStereotype.label, lang) || (dominantStereotypeId ?? concept.typeId)
    : elementType
      ? resolveBilingual(elementType.label, lang) || concept.typeId
      : concept.typeId;

  // Verzamel alle stereotype-attributen — full schema-objects houden
  // we vast (incl. label en option_labels) zodat de tabel-rendering
  // bilingual labels kan tonen ipv de raw schema-key.
  const stereotypeAttrs: Array<{
    stereotypeId: string;
    stereotypeLabel: string;
    schemas: AttributeSchema[];
  }> = [];
  for (const stId of concept.stereotypes ?? []) {
    const st = reg.getStereotype(stId);
    if (!st) continue;
    const schemas = reg.resolveStereotypeAttributes(st);
    if (schemas.length === 0) continue;
    stereotypeAttrs.push({
      stereotypeId: stId,
      stereotypeLabel: resolveBilingual(st.label, lang) || stId,
      schemas,
    });
  }

  const baseAttrCount =
    1 /* naam */ +
    (concept.description ? 1 : 0) +
    (elementType?.attributes?.length ?? 0) +
    stereotypeAttrs.reduce((acc, b) => acc + b.schemas.length, 0);

  // Klik op een rij = open het Eigenschappen-paneel (rechter sidebar)
  // voor dit concept zodat de gebruiker waarden kan bewerken. De
  // detail-tabel zelf blijft read-only. Single-click is robuust tegen
  // event-overlays (bv. ReactFlow op de canvas) — dubbelklik blijft
  // ook werken voor wie sneller is.
  const openInAttributePanel = () => {
    const ui = useUi.getState();
    ui.selectConcept(concept.id);
    ui.setAttributePanelOpen(true);
  };
  const editRowProps = {
    className: "cursor-pointer border-(--ld-rule) border-t [&:hover>td]:bg-(--ld-active)",
    onClick: openInAttributePanel,
    onDoubleClick: openInAttributePanel,
    title: t("explorer.detail.editHint"),
  } as const;

  const onDelete = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    const langKey: "nl" | "en" = lang === "en" ? "en" : "nl";
    const ok = await confirmHardDelete({
      workspaceId,
      branchId,
      conceptIds: [concept.id],
      lang: langKey,
      standaloneEdgeCount: 0,
      conceptName: (id) => useWorkspaceData.getState().store.getConcept(id)?.name,
    });
    if (!ok) return;
    try {
      await useWorkspaceData.getState().removeConcept(workspaceId, branchId, concept.id);
      onDeleted();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("removeConcept failed", err);
      window.alert(t("explorer.deleteFailed"));
    }
  };

  return (
    <div className="rounded border border-(--ld-control) bg-(--ld-surface)">
      <header className="flex items-center gap-2 border-(--ld-border) border-b bg-(--ld-active) px-2 py-1">
        <p
          className="min-w-0 flex-1 truncate font-semibold text-(--ld-text) text-xs"
          title={dominantStereotypeId ?? concept.typeId}
        >
          {typeLabel}
        </p>
        <button
          type="button"
          className="h-5 shrink-0 rounded border border-(--ld-control) px-2 text-xs text-(--ld-negative) leading-none hover:bg-(--ld-negative-bg) disabled:opacity-40"
          disabled={!workspaceId || !branchId}
          onClick={onDelete}
          hidden={readOnly}
          title={t("explorer.detail.deleteObjectTitle")}
        >
          {t("explorer.detail.deleteButton")}
        </button>
      </header>
      <details>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: <summary> is native interactief (toggelt de details); onDoubleClick is een muis-sneltoets naar het Eigenschappen-paneel */}
        <summary
          className="cursor-pointer px-2 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised)"
          onDoubleClick={openInAttributePanel}
          title={t("explorer.detail.doubleClickHint")}
        >
          {t("explorer.detail.sectionProperties")} ({baseAttrCount})
        </summary>
        <table className="w-full table-fixed text-xs">
          <colgroup>
            <col style={{ width: "40%" }} />
            <col style={{ width: "60%" }} />
          </colgroup>
          <tbody>
            <tr {...editRowProps}>
              <td className="overflow-hidden px-2 py-0.5 font-medium text-(--ld-muted)">
                {t("explorer.detail.rowName")}
              </td>
              <td
                className="overflow-hidden px-2 py-0.5 text-(--ld-text)"
                title={resolveBilingual(concept.name, lang)}
              >
                <div className="truncate">{resolveBilingual(concept.name, lang)}</div>
              </td>
            </tr>
            {concept.description ? (
              <tr {...editRowProps}>
                <td className="overflow-hidden px-2 py-0.5 font-medium text-(--ld-muted)">
                  {t("explorer.detail.rowDescription")}
                </td>
                <td className="overflow-hidden px-2 py-0.5 text-(--ld-text)">
                  <div className="break-words">
                    {resolveBilingual(concept.description, lang) || (
                      <span className="text-(--ld-muted) italic">{t("explorer.detail.empty")}</span>
                    )}
                  </div>
                </td>
              </tr>
            ) : null}
            {/* Concepts zijn shared workspace-scoped (geen branch-ownership in
                de domain-type). Branch-rij is alleen zinvol voor diagrams. */}
            {(elementType?.attributes ?? []).map((schema) => {
              const value = concept.attributes?.[schema.name];
              const schemaLabel = resolveBilingual(schema.label, lang) || schema.name;
              return (
                <tr key={`type:${schema.name}`} {...editRowProps}>
                  <td
                    className="overflow-hidden px-2 py-0.5 text-(--ld-muted)"
                    title={`${schema.name} : ${schema.type}`}
                  >
                    <div className="truncate">{schemaLabel}</div>
                  </td>
                  <td className="overflow-hidden px-2 py-0.5 text-(--ld-text)">
                    <div className="truncate">
                      {value === undefined || value === null || value === "" ? (
                        <span className="text-(--ld-muted) italic">
                          {t("explorer.detail.empty")}
                        </span>
                      ) : (
                        renderAttrValue(value, schema, lang)
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {stereotypeAttrs.length > 0
          ? stereotypeAttrs.map((bundle) => (
              <div key={bundle.stereotypeId} className="border-(--ld-border) border-t">
                {/* Verberg de bundle-kop voor het stereotype dat al in de
                 *  detail-header staat (= geen herhaling). Andere
                 *  stereotypes (multi-stereotype-bridging, M9) houden
                 *  hun kop voor scheiding tussen de attr-bundles. */}
                {bundle.stereotypeId !== dominantStereotypeId && (
                  <p
                    className="bg-(--ld-raised) px-2 py-0.5 font-medium text-(--ld-muted) text-xs"
                    title={bundle.stereotypeId}
                  >
                    {bundle.stereotypeLabel}
                  </p>
                )}
                <table className="w-full table-fixed text-xs">
                  <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "60%" }} />
                  </colgroup>
                  <tbody>
                    {bundle.schemas.map((schema) => {
                      const value = concept.attributes?.[schema.name];
                      const schemaLabel = resolveBilingual(schema.label, lang) || schema.name;
                      return (
                        <tr key={`st:${bundle.stereotypeId}:${schema.name}`} {...editRowProps}>
                          <td
                            className="overflow-hidden px-2 py-0.5 text-(--ld-muted)"
                            title={`${schema.name} : ${schema.type}`}
                          >
                            <div className="truncate">{schemaLabel}</div>
                          </td>
                          <td className="overflow-hidden px-2 py-0.5 text-(--ld-text)">
                            <div className="truncate">
                              {value === undefined || value === null || value === "" ? (
                                <span className="text-(--ld-muted) italic">
                                  {t("explorer.detail.empty")}
                                </span>
                              ) : (
                                renderAttrValue(value, schema, lang)
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))
          : null}
      </details>
      <details>
        <summary className="cursor-pointer border-(--ld-border) border-t px-2 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised)">
          {t("explorer.detail.sectionRelations")} ({detail.relations.length})
        </summary>
        {detail.relations.length === 0 ? (
          <p className="px-3 pb-2 text-(--ld-muted) text-xs italic">
            {t("explorer.detail.noRelations")}
          </p>
        ) : (
          <table className="w-full table-fixed text-xs">
            <colgroup>
              <col style={{ width: "50%" }} />
              <col style={{ width: "50%" }} />
            </colgroup>
            <tbody>
              {detail.relations.map((r: Relation) => {
                // Toon de ingevulde label of fallback naar het bilingual
                // relatie-type-label uit de registry (i.p.v. raw typeId).
                // De canvas-render zet bij niet-ingevulde labels soms de
                // placeholder "(unnamed)" — die behandelen we hier als
                // "geen label" zodat de fallback (type-label) inspringt.
                const relType = reg.getRelationType(r.typeId);
                const typeLabelBilingual = relType
                  ? resolveBilingual(relType.label, lang)
                  : r.typeId;
                const rawLabel = resolveBilingual(r.label, lang);
                const isPlaceholder =
                  !rawLabel || rawLabel === "(unnamed)" || rawLabel.trim() === "";
                const relationLabel = isPlaceholder ? typeLabelBilingual || r.typeId : rawLabel;
                const otherId =
                  r.sourceConceptId === concept.id ? r.targetConceptId : r.sourceConceptId;
                const other = store.getConcept(otherId);
                // Fallback voor folder-concepten die niet in de
                // workspace-store-hydrate zaten: server levert
                // counterpart-namen mee via detail.relatedConcepts.
                const fallbackName = detail.relatedConcepts?.find((c) => c.id === otherId)?.name;
                const otherName = other
                  ? resolveBilingual(other.name, lang) || otherId
                  : fallbackName
                    ? resolveBilingual(fallbackName, lang) || otherId
                    : otherId;
                // Pijlpunt wijst altijd naar het *andere* endpoint:
                // - concept = source → glyph wijst naar rechts (target = otherName)
                // - concept = target → glyph wijst naar links (source = otherName)
                const reversed = r.sourceConceptId !== concept.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      className="cursor-grab border-(--ld-rule) border-t hover:bg-(--ld-raised) active:cursor-grabbing"
                      draggable={!readOnly}
                      onDragStart={(e) => {
                        // Sleep de relatie naar het canvas om 'm op het
                        // actieve diagram te plaatsen — zelfde MIME als
                        // de oude RelationsPanel zodat Canvas.onDrop 'm
                        // oppakt. Als A of B nog niet op het diagram
                        // staan, retourneert placeExistingRelationOnDiagram
                        // "missing-endpoint" en faalt de drop netjes.
                        e.dataTransfer.setData("application/ae-existing-relation-id", r.id);
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                      onClick={() => {
                        // Single-click selecteert de relatie + pant het canvas
                        // ernaartoe (zelfde UX als Relations-panel). Geen
                        // AttributePanel — die opent pas bij dubbelklik om
                        // toetsenbord-flows niet te onderbreken.
                        const ui = useUi.getState();
                        ui.selectRelation(r.id);
                        ui.requestViewportFocus(r.id);
                      }}
                      onDoubleClick={() => {
                        // Dubbelklik = open AttributePanel met deze relatie
                        // geselecteerd (analoog aan Foldertree-relatie-rij
                        // en concept-rij hier in Explorer). 2026-05-26.
                        const ui = useUi.getState();
                        ui.selectRelation(r.id);
                        ui.requestViewportFocus(r.id);
                        ui.setAttributePanelOpen(true);
                      }}
                      title={t("explorer.detail.relationRowHint")}
                    >
                      <td
                        className="overflow-hidden px-2 py-0.5 text-(--ld-text)"
                        title={`${r.typeId} — ${relationLabel}`}
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <button
                            type="button"
                            className="shrink-0 text-(--ld-muted) text-xs leading-none hover:text-(--ld-text)"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRelationExpansion(r.id);
                            }}
                            aria-expanded={expandedRelationIds.has(r.id)}
                            aria-label={
                              expandedRelationIds.has(r.id)
                                ? t("explorer.detail.collapseOccurrences")
                                : t("explorer.detail.expandOccurrences")
                            }
                            title={t("explorer.detail.showOccurrencesTitle")}
                          >
                            {expandedRelationIds.has(r.id) ? "▾" : "▸"}
                          </button>
                          <BranchOwnershipDot
                            recordBranchId={r.branchId ?? null}
                            mainBranchId={mainBranchId}
                            branchNameById={branchNameById}
                          />
                          <span className="min-w-0 flex-1 truncate">{relationLabel}</span>
                        </span>
                      </td>
                      <td className="overflow-hidden px-2 py-0.5" title={otherName}>
                        <div className="flex min-w-0 items-center gap-1">
                          <RelationGlyph
                            typeId={r.typeId}
                            attributes={r.attributes}
                            reversed={reversed}
                          />
                          <span className="min-w-0 flex-1 truncate text-(--ld-text)">
                            {otherName}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {expandedRelationIds.has(r.id) && (
                      <tr className="border-(--ld-rule) border-t bg-(--ld-raised)">
                        <td colSpan={2} className="px-2 py-1">
                          {(() => {
                            // Per-instance: één rij per viewState-entry waar
                            // ref naar deze relatie wijst (multi-instance) of
                            // entry-key === r.id (canonical/legacy). Eén relatie
                            // kan meerdere instances op hetzelfde diagram hebben.
                            const occurrences: Array<{
                              diagram: ReturnType<typeof store.getDiagram>;
                              entryKey: string;
                              ve: import("@ae/domain").ViewElementState;
                              idxInDiagram: number;
                            }> = [];
                            for (const d of store.listDiagrams()) {
                              let idxInDiagram = 0;
                              for (const [k, ve] of Object.entries(d.viewState ?? {})) {
                                if (ve.position !== undefined) continue;
                                if (ve.type === "object") continue;
                                const relId = ve.ref ?? k;
                                if (relId !== r.id) continue;
                                idxInDiagram += 1;
                                occurrences.push({
                                  diagram: d,
                                  entryKey: k,
                                  ve,
                                  idxInDiagram,
                                });
                              }
                            }
                            if (occurrences.length === 0) {
                              return (
                                <p className="pl-5 text-(--ld-muted) text-xs italic">
                                  {t("explorer.detail.notPlaced")}
                                </p>
                              );
                            }
                            return (
                              <ul className="ml-5 flex min-w-0 flex-col gap-0.5 text-xs">
                                {occurrences.map(({ diagram, entryKey, idxInDiagram }) => {
                                  if (!diagram) return null;
                                  const diagramName =
                                    resolveBilingual(diagram.name, lang) || diagram.id;
                                  return (
                                    <li
                                      key={entryKey}
                                      className="flex min-w-0 items-center justify-between gap-2"
                                    >
                                      <button
                                        type="button"
                                        className="min-w-0 flex-1 truncate text-left text-(--ld-accent) hover:underline"
                                        title={t("explorer.detail.instanceTitle", {
                                          n: idxInDiagram,
                                          diagram: diagramName,
                                        })}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onJumpToDiagram({
                                            diagramId: diagram.id,
                                            diagramName: diagram.name,
                                            viewKind: diagram.viewKind,
                                            branchId: mainBranchId ?? "",
                                          });
                                          // Focus op de specifieke instance.
                                          setTimeout(() => {
                                            useUi.getState().requestViewportFocus(entryKey);
                                          }, 50);
                                        }}
                                      >
                                        <span className="text-(--ld-text)">
                                          {t("explorer.detail.instance", { n: idxInDiagram })}
                                        </span>
                                        <span className="text-(--ld-muted)"> — </span>
                                        <span>{diagramName}</span>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            );
                          })()}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </details>
      <details>
        {(() => {
          // Per-instance voorkomens (2026-05-26 — Feature C). Eén rij
          // per VIEWSTATE-INSTANCE — multi-instance concepten leveren
          // meerdere rijen voor hetzelfde diagram. Klik op een rij
          // opent het diagram én focust EXACT die instance via
          // requestViewportFocus(instanceId), niet de canonical-eerste.
          const occurrences: Array<{
            diagramId: string;
            diagramName: BilingualText;
            viewKind: string;
            branchId: string;
            instanceId: string;
            instanceIndex: number; // 1-based binnen diagram
          }> = [];
          for (const d of store.listDiagrams()) {
            let idxInDiagram = 0;
            for (const [key, ve] of Object.entries(d.viewState ?? {})) {
              if (!ve?.position) continue;
              if (ve.hidden === true) continue;
              const refId = ve.ref ?? key;
              if (refId !== concept.id) continue;
              idxInDiagram += 1;
              occurrences.push({
                diagramId: d.id,
                diagramName: d.name,
                viewKind: d.viewKind,
                branchId: mainBranchId ?? "",
                instanceId: key,
                instanceIndex: idxInDiagram,
              });
            }
          }
          return (
            <>
              <summary className="cursor-pointer border-(--ld-border) border-t px-2 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised)">
                {t("explorer.detail.sectionDiagramOccurrences")} ({occurrences.length})
              </summary>
              {occurrences.length === 0 ? (
                <p className="px-3 pb-2 text-(--ld-muted) text-xs italic">
                  {t("explorer.detail.noDiagrams")}
                </p>
              ) : (
                <ul className="flex min-w-0 flex-col">
                  {occurrences.map((o) => {
                    const diagramName = resolveBilingual(o.diagramName, lang) || o.diagramId;
                    return (
                      <li
                        key={`${o.diagramId}:${o.instanceId}`}
                        className="flex min-w-0 border-(--ld-rule) border-t px-2 py-0.5"
                      >
                        <button
                          type="button"
                          className="min-w-0 flex-1 truncate text-left text-(--ld-accent) text-xs hover:underline"
                          title={t("explorer.detail.instanceTitle", {
                            n: o.instanceIndex,
                            diagram: diagramName,
                          })}
                          onClick={() => {
                            onJumpToDiagram({
                              diagramId: o.diagramId,
                              diagramName: o.diagramName,
                              viewKind: o.viewKind,
                              branchId: o.branchId,
                            });
                            // Na het openen van het diagram focust de canvas
                            // op EXACT deze instance via requestViewportFocus.
                            // Delay zodat de hydrate eerst kan landen.
                            setTimeout(() => {
                              useUi.getState().requestViewportFocus(o.instanceId);
                            }, 50);
                          }}
                        >
                          <span className="text-(--ld-text)">
                            {t("explorer.detail.instance", { n: o.instanceIndex })}
                          </span>
                          <span className="text-(--ld-muted)"> — </span>
                          <span>{diagramName}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          );
        })()}
      </details>
      {attrChildren.length > 0 ? (
        <details open>
          <summary className="cursor-pointer px-2 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised)">
            {t("explorer.detail.sectionAttributes")} ({attrChildren.length})
          </summary>
          <ul className="flex flex-col gap-0.5 px-2 py-1">
            {attrChildren.map((kid) => {
              const isId = kid.typeId === "sharp.Identifier";
              return (
                <li key={kid.id}>
                  {/* biome-ignore lint/a11y/noStaticElementInteractions: geneste attribuut/identifier-rij; interactief (klik + drag) maar nog niet keyboard-focusbaar — treeitem-roving-a11y volgt in de tree-a11y-ronde (groep C-vervolg) */}
                  <div
                    className={`group/kid flex cursor-pointer items-center gap-1 rounded px-1 text-xs hover:bg-(--ld-raised) ${
                      kid.tombstoneInBranch ? "line-through" : ""
                    }`}
                    draggable={!kid.tombstoneInBranch && !readOnly}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(EXISTING_CONCEPT_MIME, kid.id);
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                    onClick={() => useUi.getState().selectConcept(kid.id)}
                    onDoubleClick={() => {
                      const ui = useUi.getState();
                      ui.selectConcept(kid.id);
                      ui.setAttributePanelOpen(true);
                    }}
                    title={t("explorer.detail.attrChildHint")}
                  >
                    <span className={`min-w-0 flex-1 truncate ${isId ? "underline" : ""}`}>
                      {explorerItemName(kid, lang)}
                    </span>
                    <button
                      type="button"
                      className="shrink-0 text-(--ld-muted) opacity-0 hover:text-(--ld-negative) group-hover/kid:opacity-100"
                      hidden={readOnly}
                      title={t("settings.common.delete")}
                      aria-label={t("settings.common.delete")}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!workspaceId || !branchId) return;
                        if (useSession.getState().readOnly) return;
                        const wsd = useWorkspaceData.getState();
                        void (async () => {
                          const ok = await confirmHardDelete({
                            workspaceId,
                            branchId,
                            conceptIds: [kid.id],
                            lang: lang === "en" ? "en" : "nl",
                            standaloneEdgeCount: 0,
                            // Leesbare <conceptnaam>.<attribuutnaam> i.p.v. de
                            // (vaak lege) opgeslagen attribuut-naam → anders
                            // toont de bevestiging de kale UUID. Naam live
                            // afgeleid (AttributeType-ref) via explorerItemName.
                            conceptName: () => {
                              const label = `${resolveBilingual(concept.name, lang)}.${explorerItemName(
                                kid,
                                lang,
                              )}`;
                              return { nl: label, en: label };
                            },
                            kindLabel:
                              kid.typeId === "sharp.Identifier"
                                ? t("explorer.detail.kindIdentifier")
                                : t("explorer.detail.kindAttribute"),
                          });
                          if (!ok) return;
                          await wsd.removeBatch(workspaceId, branchId, [kid.id], []);
                          onDeleted();
                        })();
                      }}
                    >
                      ×
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function DiagramDetailPanel({
  detail,
  lang,
  loading,
  branches,
  workspaceId,
  branchId,
  onSelectObject,
  onOpenDiagram,
  onDeleted,
}: {
  detail: DiagramDetailDTO;
  lang: string;
  loading: boolean;
  branches: BranchSummary[];
  workspaceId: string | null;
  branchId: string | null;
  onSelectObject: (conceptId: string) => void;
  onOpenDiagram: () => void;
  onDeleted: () => void;
}) {
  // Echte branch waarop het diagram fysiek staat (uit detail.diagram).
  // Niet de Explorer-active-branch — anders zou een main-overlay-record
  // op een feature-branch er als branch-eigen uitzien.
  const realBranchName = branches.find((b) => b.id === detail.diagram.branchId)?.name ?? null;
  const { t } = useTranslation();
  const readOnly = useSession((s) => s.readOnly);
  if (loading) {
    return <p className="text-(--ld-muted) text-xs">{t("explorer.detail.loading")}</p>;
  }
  // Klik op een rij = open het Eigenschappen-paneel voor dit diagram
  // (naam + beschrijving + viewKind editable). Branch / updatedAt zijn
  // niet user-editable maar openen 't paneel evengoed zodat de
  // gebruiker zonder te raden bij de juiste plek komt.
  const openInAttributePanel = () => {
    const ui = useUi.getState();
    ui.selectDiagram(detail.diagram.id);
    ui.setAttributePanelOpen(true);
  };
  const editRowProps = {
    className: "cursor-pointer border-(--ld-rule) border-t [&:hover>td]:bg-(--ld-active)",
    onClick: openInAttributePanel,
    onDoubleClick: openInAttributePanel,
    title: t("explorer.detail.editHint"),
  } as const;
  const diagramName = resolveBilingual(detail.diagram.name, lang);
  const onDelete = async () => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    if (
      !(await confirmDialog({
        message: t("explorer.detail.deleteDiagramConfirm", { name: diagramName }),
        danger: true,
      }))
    )
      return;
    try {
      await useWorkspaceData.getState().deleteDiagram(workspaceId, branchId, detail.diagram.id);
      onDeleted();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("deleteDiagram failed", err);
      window.alert(t("explorer.detail.deleteDiagramFailed"));
    }
  };
  return (
    <div className="rounded border border-(--ld-control) bg-(--ld-surface)">
      {/* items-start: header heeft een 2-regelige naam+viewKind-blok
          links en Open/Verwijder-buttons rechts (1-regelig). Met
          items-center liepen de buttons door de typeId-tekst — start
          legt ze op de eerste regel naast de naam. */}
      <header className="flex items-center gap-2 border-(--ld-border) border-b bg-(--ld-active) px-2 py-1">
        <div className="flex min-w-0 flex-1 items-center gap-2 font-semibold text-(--ld-text) text-xs">
          {(() => {
            // View-kind-picker inline in de header. Editbaar mits we
            // workspaceId + branchId hebben en de gebruiker write-access
            // heeft (de bestaande changeDiagramViewKind doet die check
            // server-side). Wijzigt het diagram-type live; de canvas
            // wordt door de revision-bump opnieuw opgebouwd.
            const allViewKinds = getRegistry()
              .listActiveViewKinds()
              .slice()
              .sort((a, b) =>
                resolveBilingual(a.name, lang).localeCompare(resolveBilingual(b.name, lang)),
              );
            const known = allViewKinds.find((v) => v.id === detail.diagram.viewKind);
            return (
              <select
                className="min-w-0 max-w-full truncate border-0 bg-transparent px-1 py-0 font-semibold text-(--ld-text) text-xs hover:bg-(--ld-surface) focus:bg-(--ld-surface) focus:outline focus:outline-1 focus:outline-(--ld-accent)"
                value={detail.diagram.viewKind}
                disabled={!workspaceId || !branchId}
                title={t("explorer.detail.changeTypeTitle")}
                onChange={(e) => {
                  if (!workspaceId || !branchId) return;
                  const nextVk = e.target.value;
                  if (nextVk === detail.diagram.viewKind) return;
                  void useWorkspaceData
                    .getState()
                    .changeDiagramViewKind(workspaceId, branchId, detail.diagram.id, nextVk);
                }}
              >
                {!known && (
                  <option value={detail.diagram.viewKind}>
                    {t("explorer.detail.unknownViewKind", { viewKind: detail.diagram.viewKind })}
                  </option>
                )}
                {allViewKinds.map((vk) => (
                  <option key={vk.id} value={vk.id}>
                    {resolveBilingual(vk.name, lang)}
                    {vk.custom ? " ★" : ""}
                  </option>
                ))}
              </select>
            );
          })()}
          <BranchOwnershipDot
            recordBranchId={detail.diagram.branchId}
            mainBranchId={branches.find((b) => b.is_main)?.id ?? null}
            branchNameById={(id) => branches.find((b) => b.id === id)?.name}
          />
        </div>
        <button
          type="button"
          className="h-5 shrink-0 rounded border border-(--ld-control) px-2 text-xs text-(--ld-accent) leading-none hover:bg-(--ld-active)"
          onClick={onOpenDiagram}
          title={t("explorer.detail.openTitle")}
        >
          {t("explorer.detail.openButton")}
        </button>
        <button
          type="button"
          className="h-5 shrink-0 rounded border border-(--ld-control) px-2 text-xs text-(--ld-negative) leading-none hover:bg-(--ld-negative-bg) disabled:opacity-40"
          disabled={!workspaceId || !branchId}
          onClick={onDelete}
          hidden={readOnly}
          title={t("explorer.detail.deleteDiagramTitle")}
        >
          {t("explorer.detail.deleteButton")}
        </button>
      </header>
      {workspaceId && branchId && (
        <div className="px-2">
          <RoadmapMatrixConfigBlock
            diagramId={detail.diagram.id}
            workspaceId={workspaceId}
            branchId={branchId}
            lang={lang}
          />
        </div>
      )}
      <details>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: <summary> is native interactief (toggelt de details); onDoubleClick is een muis-sneltoets naar het Eigenschappen-paneel */}
        <summary
          className="cursor-pointer px-2 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised)"
          onDoubleClick={openInAttributePanel}
          title={t("explorer.detail.doubleClickHint")}
        >
          {t("explorer.detail.sectionProperties")} (3)
        </summary>
        <table className="w-full text-xs">
          <tbody>
            <tr {...editRowProps}>
              <td className="px-2 py-0.5 font-medium text-(--ld-muted)">
                {t("explorer.detail.rowName")}
              </td>
              <td className="truncate px-2 py-0.5 text-(--ld-text)">{diagramName}</td>
            </tr>
            {/* View-kind staat in de header naast Open/Verwijder — niet
             *  herhalen hier (UX 2026-05-17). */}
            <tr {...editRowProps}>
              <td className="px-2 py-0.5 font-medium text-(--ld-muted)">
                {t("explorer.detail.rowBranch")}
              </td>
              <td className="truncate px-2 py-0.5 text-(--ld-text)">
                {realBranchName ?? (
                  <span className="font-mono text-(--ld-muted) text-xs">
                    {detail.diagram.branchId}
                  </span>
                )}
              </td>
            </tr>
            <tr {...editRowProps}>
              <td className="px-2 py-0.5 font-medium text-(--ld-muted)">
                {t("explorer.detail.rowUpdatedAt")}
              </td>
              <td className="px-2 py-0.5 text-(--ld-text)">{detail.diagram.updatedAt}</td>
            </tr>
          </tbody>
        </table>
      </details>
      <details>
        <summary className="cursor-pointer border-(--ld-border) border-t px-2 py-1 text-(--ld-muted) text-xs hover:bg-(--ld-raised)">
          {t("explorer.detail.sectionObjectReferences")} ({detail.objects.length})
        </summary>
        {detail.objects.length === 0 ? (
          <p className="px-3 pb-2 text-(--ld-muted) text-xs italic">
            {t("explorer.detail.noObjects")}
          </p>
        ) : (
          <table className="w-full text-xs">
            <tbody>
              {detail.objects.map((o) => {
                const objectName = resolveBilingual(o.name, lang);
                return (
                  <tr key={o.id} className="border-(--ld-rule) border-t">
                    <td className="px-2 py-0.5">
                      <button
                        type="button"
                        className="w-full truncate text-left text-(--ld-accent) hover:underline"
                        onClick={() => onSelectObject(o.id)}
                        title={t("explorer.detail.selectObjectTitle", { name: objectName })}
                      >
                        {objectName}
                      </button>
                    </td>
                    <td className="truncate px-2 py-0.5 font-mono text-(--ld-muted) text-xs">
                      {o.typeId}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </details>
    </div>
  );
}

function OpenInDiagramPopup({
  item,
  lang,
  onClose,
  onJump,
}: {
  item: { id: string; name: BilingualText; diagrams: DiagramOccurrenceDTO[] };
  lang: string;
  onClose: () => void;
  onJump: (occ: DiagramOccurrenceDTO) => void;
}) {
  const { t } = useTranslation();
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  // Escape sluit het popup. Capture-fase zodat andere Escape-handlers
  // (canvas-deselect, settings-close) niet eerst vuren op deze actie.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [onClose]);
  // Bij open auto-focus de eerste diagram-knop zodat Tab/Arrow/Enter
  // meteen op de lijst werken. Zonder dit blijft focus op de Explorer-
  // knop die het popup opende, en Tab gaat naar het volgende
  // Explorer-item i.p.v. naar deze popup.
  useEffect(() => {
    firstButtonRef.current?.focus();
  }, []);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-[70vh] w-full max-w-md flex-col rounded-lg bg-(--ld-surface) shadow-xl">
        <header className="flex items-center justify-between border-(--ld-border) border-b px-4 py-2">
          <h3 className="font-semibold text-(--ld-text) text-sm">
            {t("explorer.openInDiagram.title", { name: resolveBilingual(item.name, lang) })}
          </h3>
          <button
            type="button"
            className="rounded px-2 py-0.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised) hover:text-(--ld-text)"
            onClick={onClose}
            aria-label={t("settings.common.close")}
          >
            ✕
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
          {item.diagrams.length === 0 ? (
            <p className="text-(--ld-muted) text-sm">{t("explorer.openInDiagram.none")}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {item.diagrams.map((d, idx) => (
                <li key={d.diagramId}>
                  <button
                    ref={idx === 0 ? firstButtonRef : undefined}
                    type="button"
                    className="w-full rounded border border-(--ld-border) px-2 py-1 text-left text-sm hover:bg-(--ld-active) hover:text-(--ld-accent) focus:bg-(--ld-active) focus:text-(--ld-accent) focus:outline focus:outline-2 focus:outline-(--ld-accent)"
                    onClick={() => onJump(d)}
                  >
                    <span className="font-medium">{resolveBilingual(d.diagramName, lang)}</span>
                    <span className="ml-2 font-mono text-(--ld-muted) text-xs">{d.viewKind}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Klein groen bolletje als visuele indicator dat een record op een
 * non-main feature-branch staat. Niet renderen voor records op main
 * (of records zonder branch-ownership = legacy NULL = behandel als
 * main). Tooltip toont de branch-naam.
 */
/**
 * Display-naam voor een branch — strippt de `-<shortWpId>`-fallback-
 * suffix die `createWorkpackage` toevoegt wanneer "main" al bezet is
 * in de workspace (UNIQUE-constraint op `branches.name` is nog
 * workspace-scope tot D1 een table-rebuild ondersteunt). De
 * modelleur ziet daardoor altijd "main" voor de hoofdbranche van
 * de actieve workpackage, niet de technische `main-a1b2c3d4`-vorm.
 *
 * Visueel boven tekst-suffix per `feedback_visual_over_textual` —
 * de daadwerkelijke DB-naam blijft de unieke variant.
 */
function displayBranchName(
  b: { name: string; is_main: boolean; plateau_name?: BilingualText | null },
  lang: "nl" | "en",
): string {
  // Meertalige weergave: de naam van het gekoppelde Plateau-concept in de
  // actieve taal; fallback = "main" (main-rol) of de rauwe branch-naam.
  if (b.plateau_name) {
    const nm = resolveBilingual(b.plateau_name, lang);
    if (nm) return nm;
  }
  return b.is_main ? "main" : b.name;
}

function BranchOwnershipDot({
  recordBranchId,
  mainBranchId,
  branchNameById,
}: {
  recordBranchId: string | null | undefined;
  mainBranchId: string | null | undefined;
  branchNameById: (id: string) => string | undefined;
}) {
  const { t } = useTranslation();
  if (!recordBranchId) return null;
  if (recordBranchId === mainBranchId) return null;
  const branchName = branchNameById(recordBranchId);
  if (!branchName) return null;
  return (
    <span
      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-(--ld-positive)"
      title={t("explorer.branchOwnTitle", { branch: branchName })}
    />
  );
}

// uniqueDiagrams (vóór 2026-05-26) dedupte de DiagramOccurrenceDTO-
// lijst op diagramId. Vervangen door per-instance-render — elke
// instance krijgt z'n eigen rij — dus de helper is niet langer in
// gebruik en is verwijderd.

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  // BilingualText-shape herkennen: object met alleen nl/en-velden.
  if (isBilingualLike(v)) {
    return resolveBilingual(v as BilingualText, "nl") || JSON.stringify(v);
  }
  return JSON.stringify(v);
}

function isBilingualLike(v: unknown): v is BilingualText {
  if (!v || typeof v !== "object") return false;
  const keys = Object.keys(v as Record<string, unknown>);
  if (keys.length === 0) return false;
  return keys.every((k) => k === "nl" || k === "en");
}

/**
 * Render een attribuut-waarde rekening houdend met de schema. Voor
 * een enum gebruiken we option_labels (bilingual) als die er is, voor
 * een BilingualText-waarde resolven we via lang, en anders fallback
 * naar `stringify`.
 */
function renderAttrValue(value: unknown, schema: AttributeSchema, lang: string): string {
  if (
    typeof value === "string" &&
    schema.type.startsWith("enum") &&
    schema.option_labels?.[value]
  ) {
    return resolveBilingual(schema.option_labels[value]!, lang) || value;
  }
  if (isBilingualLike(value)) {
    return resolveBilingual(value as BilingualText, lang) || JSON.stringify(value);
  }
  return stringify(value);
}
